import axios from 'axios';

export const CREATOR_CHUNK_BYTES = 8 * 1024 * 1024;
export const creatorUploadStageLabel = stage => ({ hash: 'Analizando...', probe: 'Analizando...', processing: 'Analizando...',
  proxy: 'Preparando vista previa...', thumbnail: 'Preparando miniatura...', waveform: 'Preparando audio...', ready: 'Listo' })[stage] || 'Preparando proxy...';
const sessions = new WeakMap();
const retryable = error => !error.response || [408, 409, 429, 502, 503, 504, 524].includes(error.response.status);
const cancelled = () => Object.assign(new Error('La carga se cancelo.'), { code: 'ERR_CANCELED' });
const delay = (ms, signal) => new Promise((resolve, reject) => {
  const abort = () => { clearTimeout(timer); reject(cancelled()); };
  const timer = setTimeout(() => { signal?.removeEventListener('abort', abort); resolve(); }, ms);
  if (signal?.aborted) abort(); else signal?.addEventListener('abort', abort, { once: true });
});
const identifier = () => Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join('');
async function digest(blob) {
  const bytes = blob.arrayBuffer ? await blob.arrayBuffer() : await new Promise((resolve, reject) => {
    const reader = new FileReader(); reader.onload = () => resolve(reader.result); reader.onerror = () => reject(reader.error); reader.readAsArrayBuffer(blob);
  });
  return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), byte => byte.toString(16).padStart(2, '0')).join('');
}
function uploadError(code, message, status = 422, metadata = {}) {
  return Object.assign(new Error(message), { code, response: { status, data: { detail: { code, message, ...metadata } } } });
}
export function checkCreatorUpload(file, capabilities) {
  const maximum = capabilities?.limits?.max_upload_bytes;
  if (Number.isFinite(maximum) && maximum > 0 && file.size > maximum) {
    throw uploadError('CREATOR_FILE_TOO_LARGE', `El archivo supera el limite de ${(maximum / 1048576).toFixed(0)} MiB.`, 413, { max_bytes: maximum });
  }
}

export async function uploadCreatorChunks(url, headers, file, onUploadProgress, { signal, onStage, capabilities } = {}) {
  checkCreatorUpload(file, capabilities);
  const key = `${url}:${headers['X-Creator-Token']}`;
  let entries = sessions.get(file);
  if (!entries) { entries = new Map(); sessions.set(file, entries); }
  if (!entries.has(key)) entries.set(key, identifier());
  const uploadId = entries.get(key), root = `${url}/uploads/${uploadId}`;
  const config = { headers, signal, timeout: 0 };
  const progress = received => onUploadProgress?.({ loaded: Math.min(file.size, received), total: file.size });
  const ensureActive = state => {
    if (state.state === 'error' || state.state === 'cancelled') {
      entries.delete(key);
      const detail = state.error || state;
      throw uploadError(detail.code || 'CREATOR_PROXY_FAILED', detail.message || 'No pudimos preparar el video.', detail.status_code || 422);
    }
  };
  const attempt = async action => {
    for (let count = 0; ; count += 1) {
      if (signal?.aborted) throw cancelled();
      try { return await action(); } catch (error) {
        if (signal?.aborted || count >= 2 || !retryable(error)) throw error;
        await delay(400 * (count + 1), signal);
      }
    }
  };
  try {
    onStage?.('uploading');
    const admitted = await attempt(() => axios.post(`${url}/uploads`, { filename: file.name, size_bytes: file.size, upload_id: uploadId }, config));
    let state = admitted.data;
    ensureActive(state);
    const chunkBytes = Math.min(CREATOR_CHUNK_BYTES, Math.max(1, state.chunk_bytes || capabilities?.uploads?.chunk_bytes || CREATOR_CHUNK_BYTES));
    let offset = state.received_bytes || 0;
    if (!Number.isSafeInteger(offset) || offset < 0 || offset > file.size) throw uploadError('CREATOR_SERVER_ERROR', 'El servidor devolvio un avance de carga invalido.', 503);
    progress(offset);
    while (offset < file.size && state.state !== 'ready') {
      const block = file.slice(offset, Math.min(file.size, offset + chunkBytes));
      const sha256 = await digest(block);
      const result = await attempt(() => axios.put(`${root}/chunks?offset=${offset}`, block, {
        ...config, headers: { ...headers, 'Content-Type': 'application/octet-stream', 'X-Chunk-SHA256': sha256 },
        onUploadProgress: event => progress(offset + Math.min(block.size, event.loaded || 0)),
      }));
      const next = result.data.received_bytes;
      if (!Number.isSafeInteger(next) || next < offset + block.size || next > file.size) throw uploadError('CREATOR_SERVER_ERROR', 'El servidor no confirmo el bloque de video.', 503);
      offset = next; progress(offset);
    }
    if (state.state !== 'ready') state = (await attempt(() => axios.post(`${root}/complete`, {}, config))).data;
    while (state.state !== 'ready') {
      ensureActive(state);
      onStage?.(state.phase || 'processing');
      await delay(700, signal);
      state = (await attempt(() => axios.get(root, config))).data;
    }
    if (!state.asset || !state.project) throw uploadError('CREATOR_SERVER_ERROR', 'La carga termino sin confirmar el medio.', 503);
    onStage?.('ready'); progress(file.size);
    return { asset: state.asset, project: state.project };
  } catch (error) {
    if (signal?.aborted) {
      entries.delete(key);
      await axios.delete(root, { headers, timeout: 30000 }).catch(() => {});
      throw cancelled();
    }
    throw error;
  }
}

export function creatorUploadError(error, fallback = 'No pudimos procesar el video.') {
  if (error?.code === 'ERR_CANCELED' || error?.name === 'AbortError') return 'La carga se cancelo.';
  const status = error?.response?.status, detail = error?.response?.data?.detail;
  const code = detail?.code || error?.code;
  if (status === 405 || code === 'CREATOR_METHOD_MISMATCH') return 'La ruta de carga del servidor no acepta este metodo. Revisa la version o configuracion de la API.';
  if (status === 413 && detail?.message && code && !['too_large', 'chunk_too_large', 'CREATOR_FILE_TOO_LARGE'].includes(code)) return detail.message;
  if (status === 413 || ['too_large', 'CREATOR_FILE_TOO_LARGE'].includes(code)) return detail?.max_bytes
    ? `El archivo supera el limite de ${(detail.max_bytes / 1048576).toFixed(0)} MiB.` : 'La solicitud supera el limite de carga del servidor o del proxy.';
  if (status === 507 || ['insufficient_storage', 'CREATOR_INSUFFICIENT_STORAGE'].includes(code)) return 'No hay espacio temporal suficiente para importar el video.';
  if (!error?.response && !['CREATOR_PROXY_FAILED', 'CREATOR_CORRUPT_MEDIA'].includes(code)) return 'La carga se interrumpio. Reintenta para reanudarla.';
  return (typeof detail === 'object' && detail?.message) || (status === 422 ? 'No pudimos leer este archivo de video.' : fallback);
}
